import { colors } from "@styles/colors";

/**
 * 配送員 marker 的圖示：俯視角的小貨卡，車頭朝上（0 度 = 正北）。
 *
 * 畫成俯視角是因為整個圖示會跟著 bearing 轉——側視圖一轉就變成側躺或倒立，
 * 看起來像壞掉而不是在轉彎。後台 FleetMap 轉的也是俯視角的車。
 *
 * 用 SVG 不用 emoji：emoji 由各家系統字型決定長相，🚚 在 iOS 與 Android 是兩張圖，
 * 大小與基線也不受控，縮到 20px 常常糊成一團——同一張地圖在不同手機上會長得不一樣。
 *
 * 後斗那四個白點是鋼瓶。客戶一眼要看出來的不只是「有人在移動」，而是「瓦斯在路上」。
 */

export const DRIVER_ICON_SIZE = 40;

const DARK = colors.text;

export const DRIVER_ICON_SVG = `
  <rect x="10.6" y="9.8" width="2.8" height="6.2" rx="1.4" fill="${DARK}" opacity=".8"></rect>
  <rect x="26.6" y="9.8" width="2.8" height="6.2" rx="1.4" fill="${DARK}" opacity=".8"></rect>
  <rect x="10.6" y="23.2" width="2.8" height="6.2" rx="1.4" fill="${DARK}" opacity=".8"></rect>
  <rect x="26.6" y="23.2" width="2.8" height="6.2" rx="1.4" fill="${DARK}" opacity=".8"></rect>
  <rect x="12.2" y="2.6" width="15.6" height="34.8" rx="3.8" fill="${colors.primary}"></rect>
  <path d="M14.6 11.4c1.3-1.2 3.2-1.8 5.4-1.8s4.1.6 5.4 1.8l-.7 2.8H15.3z" fill="${colors.light}" opacity=".92"></path>
  <rect x="13.8" y="18.2" width="12.4" height="17.4" rx="2" fill="${colors.grayDark}" opacity=".35"></rect>
  <circle cx="17" cy="23.6" r="2.3" fill="${colors.light}" opacity=".9"></circle>
  <circle cx="23" cy="23.6" r="2.3" fill="${colors.light}" opacity=".9"></circle>
  <circle cx="17" cy="30.4" r="2.3" fill="${colors.light}" opacity=".9"></circle>
  <circle cx="23" cy="30.4" r="2.3" fill="${colors.light}" opacity=".9"></circle>`;

/**
 * 車頭方向那一層的鉤子：逐幀旋轉是直接改這個元素的 transform，不重建整個圖示。
 *
 * 會轉的那層一定要是 <div>，不能把 class 掛在 <svg> 上：SVG 元素的 CSS
 * transform-origin 各家預設值不一致（Safari 用 0 0），掛在 svg 上會變成繞左上角轉，
 * 在 LINE 的 iOS webview 裡就是壞的。
 */
export const VEHICLE_CLASS = "lpg-driver-vehicle";

/**
 * 補回小數像素的那一層。
 * Leaflet 的 Marker.update() 是 latLngToLayerPoint(...).round()，marker 每次定位都被
 * 貼到整數像素上；這一層吃 [-0.5, 0.5] 的餘數把小數補回去。
 */
export const SUBPIXEL_CLASS = "lpg-driver-subpixel";

/** 水滴狀圖釘（實心，不挖洞——洞裡要放房子） */
const PIN_PATH = "M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z";
/** 圖釘裡的房子：目的地是「你家」，房子比圓點更快讀懂 */
const HOUSE_PATH = "M12 5.4 7.9 9v3.9h8.2V9z";

export const DESTINATION_ICON_SIZE = 30;
/** 圖釘的錨點在尖端：30px 的圖裡尖端大約在 y=28 */
export const DESTINATION_ICON_ANCHOR_Y = 28;

/**
 * 配送員圖示的 HTML。divIcon 吃的是 HTML 字串，所以圖示在這裡定義。
 */
export const driverIconHtml = (): string => {
  const d = DRIVER_ICON_SIZE;
  return `<div style="position:relative;width:${d}px;height:${d}px;transition:opacity .3s">
    <div class="${SUBPIXEL_CLASS}" style="position:absolute;inset:0;will-change:transform">
    <!--
      落影用 SVG 的放射漸層，不用 CSS 的 filter: blur()。
      這一層的父元素每次更新都在改 transform，而模糊是填充率很吃重的運算，
      掛在會動的元素上等於每次都要重新點陣化一次。漸層則是當成一般的圖形畫出來，
      成本跟畫一個圓差不多。影子也刻意不跟著轉：車子轉向時影子不該跟著甩。
    -->
    <svg width="${d}" height="${d}" viewBox="0 0 ${d} ${d}" aria-hidden="true"
      style="position:absolute;inset:0;display:block">
      <defs>
        <radialGradient id="lpgDriverShadow">
          <stop offset="0" stop-color="${colors.text}" stop-opacity=".3"></stop>
          <stop offset=".55" stop-color="${colors.text}" stop-opacity=".2"></stop>
          <stop offset="1" stop-color="${colors.text}" stop-opacity="0"></stop>
        </radialGradient>
      </defs>
      <circle cx="${d / 2}" cy="${d / 2}" r="12" fill="url(#lpgDriverShadow)"></circle>
    </svg>
    <div class="${VEHICLE_CLASS}" style="position:absolute;inset:0;transform:rotate(0deg);
      transform-origin:50% 50%;will-change:transform">
      <svg width="${d}" height="${d}" viewBox="0 0 ${d} ${d}" aria-hidden="true"
        style="position:absolute;inset:0;display:block">${DRIVER_ICON_SVG}</svg>
    </div>
    </div>
  </div>`;
};

/** 目的地（你家）的圖示 HTML。 */
export const destinationIconHtml = (): string =>
  `<svg width="${DESTINATION_ICON_SIZE}" height="${DESTINATION_ICON_SIZE}" viewBox="0 0 24 24"
      aria-hidden="true" style="display:block;filter:drop-shadow(0 2px 3px rgba(0,0,0,.3))">
    <path d="${PIN_PATH}" fill="${colors.danger}"></path>
    <path d="${HOUSE_PATH}" fill="${colors.light}"></path>
  </svg>`;
